import { message } from 'antd';

import { toPaginate, translate } from '@/utils/infrastructure';
import { RequestTracker } from '@/utils/infrastructure/communication';

import {
  api,
  buildDocumentsFilter,
  buildFolderFilter,
  deleteDocumentItems,
  DOCUMENT_MANAGER_NAMESPACE,
  DOCUMENT_SEARCH_FIELDS,
  exportDocuments,
  fetchDocumentGroups,
  GROUP_LEVELS,
  MAX_DOCUMENT_BULK_EXPORT_ITEM_COUNT,
  MAX_DOCUMENT_BULK_EXPORT_TOTAL_BYTES,
  MAX_DOCUMENT_BULK_ITEMS_COUNT,
  moveDocumentItems,
  uploadTemporaryFile,
} from './service';
import {
  collectDirectoryPaths,
  dirnamePath,
  entriesFromUploadFileList,
  sanitizeFolderName,
} from './uploadHelpers';

const meta = { page: 1, limit: 25, count: 0, totalPages: 0 };

/** True when HttpService / RequestTracker aborted the in-flight call. */
const isRequestAborted = (error) =>
  !!(error?.aborted || error?.name === 'AbortError' || error?.code === 'ERR_CANCELED');

/** Loading flags depending on whether the view lists virtual groups or folders/docs. */
const loadingForView = (groupBy, groupPath = []) => {
  const levels = GROUP_LEVELS[groupBy] || [];
  const atGroupsLevel = groupBy !== 'folder' && groupPath.length < levels.length;
  return {
    loadingFolders: groupBy === 'folder',
    loadingDocuments: groupBy === 'folder' || !atGroupsLevel,
    loadingGroups: atGroupsLevel,
    loadingMoreDocuments: false,
    loadingMoreGroups: false,
  };
};

/**
 * Clears lists, bumps contentEpoch, and starts the destination skeleton.
 * Used by resetContents and navigation (tab / folder / group).
 */
const clearViewLists = (state, groupBy, groupPath = []) => ({
  ...state,
  contentEpoch: (state.contentEpoch || 0) + 1,
  folders: [],
  documents: [],
  groups: [],
  pagination: meta,
  groupsPagination: meta,
  ...loadingForView(groupBy, groupPath),
});

// Advanced list filters (same pattern as /applications).
const emptyFilters = {
  fileTypeClassification: undefined, // AttachmentFileTypeClassification
  source: undefined, // DocumentSource
  processId: undefined, // string | string[]
  creator: undefined, // owner userId (contains on JSON creator)
  dateRange: undefined, // [moment, moment] on `created`
};

const initialState = {
  // Real folder navigation
  currentFolderId: null, // null = root / "My Drive"
  folderPath: [], // breadcrumbs: [{ id, name }] (excludes root)
  // Grouping by virtual folders (process / request / form)
  groupBy: 'folder', // folder | process | processInstance | form | entity | list | chatThread
  groupPath: [], // virtual drill-down: [{ key, id, name }]
  groups: [], // groups at the current level
  groupsPagination: meta,
  // Data
  folders: [], // subfolders of the current folder
  documents: [], // documents in the current folder (paginated)
  allFolders: [], // flat catalog for the "Move" picker
  pagination: meta,
  // UI / query
  query: { page: 1, limit: 25, orderBy: 'created desc', search: '' },
  filters: { ...emptyFilters },
  // Start true so the first paint shows skeleton before mount fetch runs.
  loadingFolders: true,
  loadingDocuments: true,
  loadingGroups: false,
  loadingMoreDocuments: false,
  loadingMoreGroups: false,
  // Upload progress (null = idle)
  uploadProgress: null, // { phase, current, total, name }
  // Active bulk-export task tracked for the floating panel (null = idle)
  exportTask: null, // { taskId, taskNumber, status, taskOutput, observations, requestedDocumentCount }
  // "Mis exportaciones" drawer list
  exportTasks: [],
  exportTasksPagination: meta,
  loadingExportTasks: false,
  loadingMoreExportTasks: false,
  // Load generation: incremented on each reset/navigation to discard
  // stale responses from the previous view.
  contentEpoch: 0,
};

const Model = {
  namespace: 'documentManager',

  state: initialState,

  effects: {
    /**
     * Coordinates content loading based on the current view mode.
     * takeLatest: when switching tabs/paths quickly, cancels the previous
     * fetch so a wipe/stale response does not clear the new view.
     *
     * skipReset: navigation (setGroupBy / enterGroup / enterFolder /
     * navigateToPath) already cleared lists + epoch; do not bump again.
     */
    fetchContents: [
      function* ({ payload = {} }, { put, select }) {
        const readPriority = !!payload.readPriority;
        const append = !!payload.append;
        const silent = !!payload.silent;

        let state = yield select((s) => s.documentManager);
        const { query, filters } = state;

        const nextQuery = {
          page: payload.page ?? query.page,
          limit: payload.limit ?? query.limit,
          orderBy: payload.orderBy ?? query.orderBy,
          // Use `in` so search: '' (chip clear) is not lost.
          search: 'search' in payload ? payload.search || '' : query.search,
        };

        if (payload.filters) {
          const nextFilters = { ...filters, ...payload.filters };
          nextQuery.page = 1;
          yield put({ type: 'saveFilters', payload: nextFilters });
        }

        yield put({ type: 'saveQuery', payload: nextQuery });

        // When changing folder/group/mode, clear the list immediately.
        // If navigation already reset (`skipReset`), do not bump epoch again.
        const shouldReset = payload.reset && !append && !payload.soft && !payload.skipReset;
        if (shouldReset) {
          yield put({ type: 'resetContents' });
        }

        // Abort in-flight GETs for this page (same pattern as applications
        // RequestTracker). Skip on append/silent so infinite scroll and
        // post-mutation soft refresh do not kill uploads or the prior page.
        if (!append && !silent && (shouldReset || payload.skipReset || payload.reset)) {
          try {
            RequestTracker.getInstance().cancelAll(DOCUMENT_MANAGER_NAMESPACE);
          } catch (e) {
            /* ignore */
          }
        }

        // Re-read after yields: another setGroupBy/enter* may have run in parallel.
        state = yield select((s) => s.documentManager);
        const { groupBy, groupPath, contentEpoch } = state;
        const folderId = 'folderId' in payload ? payload.folderId : state.currentFolderId;
        const nextFilters = state.filters;
        const levels = GROUP_LEVELS[groupBy] || [];
        const atGroupsLevel = groupBy !== 'folder' && groupPath.length < levels.length;

        // Infinite scroll: only request the next page of docs/groups.
        if (append) {
          if (atGroupsLevel) {
            yield put({
              type: 'fetchGroups',
              payload: {
                level: levels[groupPath.length],
                parentGroups: groupPath,
                ...nextQuery,
                filters: nextFilters,
                readPriority,
                append: true,
                contentEpoch,
                silent,
              },
            });
          } else {
            yield put({
              type: 'fetchDocuments',
              payload: {
                ...(groupBy === 'folder'
                  ? { folderId }
                  : { folderScope: false, groups: groupPath }),
                ...nextQuery,
                filters: nextFilters,
                readPriority,
                append: true,
                contentEpoch,
                silent,
              },
            });
          }
          return;
        }

        if (groupBy === 'folder') {
          yield put({
            type: 'fetchFolders',
            payload: { folderId, search: nextQuery.search, readPriority, contentEpoch, silent },
          });
          yield put({
            type: 'fetchDocuments',
            payload: {
              folderId,
              ...nextQuery,
              filters: nextFilters,
              readPriority,
              contentEpoch,
              silent,
            },
          });
          return;
        }

        if (atGroupsLevel) {
          yield put({
            type: 'fetchGroups',
            payload: {
              level: levels[groupPath.length],
              parentGroups: groupPath,
              ...nextQuery,
              filters: nextFilters,
              readPriority,
              contentEpoch,
              silent,
            },
          });
        } else {
          yield put({
            type: 'fetchDocuments',
            payload: {
              folderScope: false,
              groups: groupPath,
              ...nextQuery,
              filters: nextFilters,
              readPriority,
              contentEpoch,
              silent,
            },
          });
        }
      },
      { type: 'takeLatest' },
    ],

    *fetchGroups({ payload }, { call, put, select }) {
      const append = !!payload.append;
      const silent = !!payload.silent;
      const epoch = payload.contentEpoch;
      if (!silent) {
        yield put({
          type: 'setLoading',
          payload: append ? { moreGroups: true } : { groups: true },
        });
      }
      let aborted = false;
      try {
        const res = yield call(fetchDocumentGroups, payload);
        const currentEpoch = yield select((s) => s.documentManager.contentEpoch);
        if (epoch != null && epoch !== currentEpoch) return;
        yield put({
          type: 'saveGroups',
          payload: {
            items: res.items,
            page: payload.page,
            limit: payload.limit,
            totalCount: res.totalCount,
            append,
            contentEpoch: epoch,
          },
        });
      } catch (e) {
        // Aborted: leave loading on so the replacement request keeps the skeleton.
        if (isRequestAborted(e)) {
          aborted = true;
          return;
        }
        throw e;
      } finally {
        if (!silent && !aborted) {
          const currentEpoch = yield select((s) => s.documentManager.contentEpoch);
          if (epoch == null || epoch === currentEpoch) {
            yield put({
              type: 'setLoading',
              payload: append ? { moreGroups: false } : { groups: false },
            });
          }
        }
      }
    },

    *fetchFolders({ payload }, { call, put, select }) {
      const { folderId, search, readPriority, contentEpoch: epoch, silent } = payload;
      if (!silent) {
        yield put({ type: 'setLoading', payload: { folders: true } });
      }
      let aborted = false;
      try {
        const res = yield call(api.getAll, {
          route: 'folder/paged',
          filter: buildFolderFilter(folderId),
          orderBy: 'name asc',
          page: 1,
          limit: 200,
          ...(search && { search, searchSelect: ['name'] }),
          ...(readPriority && { readPriority: true }),
        });
        const currentEpoch = yield select((s) => s.documentManager.contentEpoch);
        if (epoch != null && epoch !== currentEpoch) return;
        yield put({
          type: 'saveFolders',
          payload: { items: res?.items || [], contentEpoch: epoch },
        });
      } catch (e) {
        if (isRequestAborted(e)) {
          aborted = true;
          return;
        }
        throw e;
      } finally {
        if (!silent && !aborted) {
          const currentEpoch = yield select((s) => s.documentManager.contentEpoch);
          if (epoch == null || epoch === currentEpoch) {
            yield put({ type: 'setLoading', payload: { folders: false } });
          }
        }
      }
    },

    *fetchDocuments({ payload }, { call, put, select }) {
      const {
        folderId,
        folderScope = true,
        groups = [],
        page = 1,
        limit = 25,
        orderBy = 'created desc',
        search,
        filters,
        readPriority,
        append = false,
        contentEpoch: epoch,
        silent = false,
      } = payload;
      if (!silent) {
        yield put({
          type: 'setLoading',
          payload: append ? { moreDocuments: true } : { documents: true },
        });
      }
      let aborted = false;
      try {
        const res = yield call(api.getAll, {
          route: 'document/paged',
          filter: buildDocumentsFilter({ folderId, folderScope, groups, ...(filters || {}) }),
          orderBy,
          page,
          limit,
          ...(search && { search, searchSelect: DOCUMENT_SEARCH_FIELDS }),
          ...(readPriority && { readPriority: true }),
        });
        const currentEpoch = yield select((s) => s.documentManager.contentEpoch);
        if (epoch != null && epoch !== currentEpoch) return;
        yield put({
          type: 'saveDocuments',
          payload: {
            items: res?.items || [],
            page,
            limit,
            totalCount: res?.totalCount || 0,
            append,
            contentEpoch: epoch,
          },
        });
      } catch (e) {
        if (isRequestAborted(e)) {
          aborted = true;
          return;
        }
        throw e;
      } finally {
        if (!silent && !aborted) {
          const currentEpoch = yield select((s) => s.documentManager.contentEpoch);
          if (epoch == null || epoch === currentEpoch) {
            yield put({
              type: 'setLoading',
              payload: append ? { moreDocuments: false } : { documents: false },
            });
          }
        }
      }
    },

    /** Flat folder catalog (for the destination picker in "Move"). */
    *fetchAllFolders(_action, { call, put }) {
      const res = yield call(api.getAll, {
        route: 'folder/paged',
        orderBy: 'name asc',
        page: 1,
        limit: 500,
      });
      yield put({ type: 'saveAllFolders', payload: res?.items || [] });
    },

    // ── Folders ────────────────────────────────────────────────────────
    *createFolder({ payload }, { call, put, select }) {
      const parentFolderId = yield select((s) => s.documentManager.currentFolderId);
      const created = yield call(api.create, {
        route: 'folder',
        data: {
          name: payload.name,
          parentFolderId,
          ...(payload.color ? { color: payload.color } : {}),
        },
      });
      message.success(translate('documents.msg.folderCreated'));

      const id = created?.id || created?.data?.id;
      if (id) {
        yield put({
          type: 'upsertFolder',
          payload: {
            ...(created?.data && typeof created.data === 'object' ? created.data : created),
            id,
            name: payload.name,
            ...(payload.color ? { color: payload.color } : {}),
            parentFolderId: parentFolderId ?? null,
          },
        });
      } else {
        // No id in the response: silent refresh (does not clear the view).
        yield put({
          type: 'fetchContents',
          payload: { page: 1, readPriority: true, silent: true },
        });
      }
    },

    *renameFolder({ payload }, { call, put }) {
      yield call(api.updateById, {
        id: payload.id,
        route: 'folder',
        data: { name: payload.name, ...(payload.color ? { color: payload.color } : {}) },
      });
      message.success(translate('documents.msg.folderRenamed'));
      yield put({
        type: 'patchFolder',
        payload: {
          id: payload.id,
          name: payload.name,
          ...(payload.color ? { color: payload.color } : {}),
        },
      });
    },

    *moveFolder({ payload }, { call, put, select }) {
      yield call(api.updateById, {
        id: payload.id,
        route: 'folder',
        data: { parentFolderId: payload.parentFolderId ?? null },
      });
      message.success(translate('documents.msg.folderMoved'));

      const currentFolderId = yield select((s) => s.documentManager.currentFolderId);
      const dest = payload.parentFolderId ?? null;
      // If it left the current folder, remove it from the list; if the destination
      // is the current folder, no need to update the view.
      if ((currentFolderId ?? null) !== dest) {
        yield put({ type: 'removeFolder', payload: { id: payload.id } });
      }
    },

    *deleteFolder({ payload }, { call, put }) {
      yield call(api.remove, { id: payload.id, route: 'folder' });
      message.success(translate('documents.msg.folderDeleted'));
      yield put({ type: 'removeFolder', payload: { id: payload.id } });
    },

    // ── Documents ────────────────────────────────────────────────────
    /**
     * Uploads files (and optionally a folder hierarchy).
     * `payload.entries` = [{ file, relativePath, isEmptyDir? }]
     * or `payload.files` = AntD Upload fileList (normalized).
     */
    *uploadDocuments({ payload }, { call, put, select }) {
      const rootFolderId = yield select((s) => s.documentManager.currentFolderId);
      const entries = payload?.entries || entriesFromUploadFileList(payload?.files || []);

      if (!entries.length) return;

      // path → folderId ('' = current folder / root)
      const folderCache = new Map([['', rootFolderId ?? null]]);

      try {
        const dirPaths = collectDirectoryPaths(entries);
        if (dirPaths.length) {
          yield put({
            type: 'setUploadProgress',
            payload: { phase: 'folders', current: 0, total: dirPaths.length, name: '' },
          });
        }

        for (let i = 0; i < dirPaths.length; i++) {
          const dirPath = dirPaths[i];
          if (folderCache.has(dirPath)) continue;

          const parts = dirPath.split('/');
          const folderName = sanitizeFolderName(parts[parts.length - 1]);
          const parentPath = parts.slice(0, -1).join('/');
          const parentFolderId = folderCache.has(parentPath)
            ? folderCache.get(parentPath)
            : rootFolderId ?? null;

          yield put({
            type: 'setUploadProgress',
            payload: {
              phase: 'folders',
              current: i + 1,
              total: dirPaths.length,
              name: folderName,
            },
          });

          const created = yield call(api.create, {
            route: 'folder',
            data: {
              name: folderName,
              parentFolderId: parentFolderId ?? null,
            },
          });
          const newId = created?.id || created?.data?.id;
          if (!newId) {
            throw new Error('Folder create did not return id');
          }
          folderCache.set(dirPath, newId);
        }

        const fileEntries = entries.filter((e) => e.file && !e.isEmptyDir);
        yield put({
          type: 'setUploadProgress',
          payload: { phase: 'files', current: 0, total: fileEntries.length, name: '' },
        });

        for (let i = 0; i < fileEntries.length; i++) {
          const entry = fileEntries[i];
          const dir = dirnamePath(entry.relativePath);
          const folderId = folderCache.has(dir) ? folderCache.get(dir) : rootFolderId ?? null;
          const fileName = entry.file?.name || entry.relativePath || '';

          yield put({
            type: 'setUploadProgress',
            payload: {
              phase: 'files',
              current: i + 1,
              total: fileEntries.length,
              name: fileName,
            },
          });

          const attachment = yield call(uploadTemporaryFile, entry.file);
          yield call(api.create, {
            route: 'document',
            data: { folderId: folderId ?? null, attachment },
          });
        }

        message.success(translate('documents.msg.documentUploaded'));
        // Soft + silent: refresh the list without clearing or showing a loading spinner.
        yield put({
          type: 'fetchContents',
          payload: { page: 1, readPriority: true, silent: true },
        });
      } finally {
        yield put({ type: 'setUploadProgress', payload: null });
      }
    },

    *renameDocument({ payload }, { call, put }) {
      yield call(api.updateById, {
        id: payload.id,
        route: 'document',
        data: { fileName: payload.fileName },
      });
      message.success(translate('documents.msg.documentRenamed'));
      yield put({
        type: 'patchDocument',
        payload: { id: payload.id, fileName: payload.fileName },
      });
    },

    *moveDocument({ payload }, { call, put, select }) {
      // UX guide requires the dedicated POST /document/{id}/move endpoint.
      yield call(api.apiRoute, {
        route: `document/${payload.id}/move`,
        method: 'post',
        data: { folderId: payload.folderId ?? null },
      });
      message.success(translate('documents.msg.documentMoved'));

      const currentFolderId = yield select((s) => s.documentManager.currentFolderId);
      const groupBy = yield select((s) => s.documentManager.groupBy);
      const dest = payload.folderId ?? null;
      // In folder view: if the destination is not the current folder, remove it.
      // In grouped views: the doc no longer belongs in the filtered list → remove.
      if (groupBy !== 'folder' || (currentFolderId ?? null) !== dest) {
        yield put({ type: 'removeDocument', payload: { id: payload.id } });
      }
    },

    *deleteDocument({ payload }, { call, put }) {
      yield call(api.remove, { id: payload.id, route: 'document' });
      message.success(translate('documents.msg.documentDeleted'));
      yield put({ type: 'removeDocument', payload: { id: payload.id } });
    },

    /**
     * Starts bulk export for a typed selection (documents and/or folders).
     * Payload: { items: [{ id, type }], totalBytes? }.
     */
    *exportSelectedDocuments({ payload }, { call, put }) {
      const items = (payload?.items || []).filter(
        (it) => it?.id && (it.type === 'document' || it.type === 'folder'),
      );
      if (!items.length) {
        message.warning(translate('documents.export.emptySelection'));
        return null;
      }
      if (items.length > MAX_DOCUMENT_BULK_EXPORT_ITEM_COUNT) {
        message.error(
          translate('documents.export.maxCount', { max: MAX_DOCUMENT_BULK_EXPORT_ITEM_COUNT }),
        );
        return null;
      }
      const totalBytes = Number(payload?.totalBytes) || 0;
      if (totalBytes > MAX_DOCUMENT_BULK_EXPORT_TOTAL_BYTES) {
        message.error(translate('documents.export.maxSize'));
        return null;
      }

      try {
        const res = yield call(exportDocuments, items);
        const task = {
          taskId: res?.taskId || res?.id,
          taskNumber: res?.taskNumber || res?.number,
          status: res?.status || 'pending',
          requestedDocumentCount: res?.requestedDocumentCount ?? items.length,
          taskOutput: null,
          observations: null,
          message: res?.message || null,
        };
        yield put({ type: 'setExportTask', payload: task });
        message.success(
          translate('documents.export.started', {
            taskNumber: task.taskNumber || '',
            count: task.requestedDocumentCount,
          }),
        );
        return task;
      } catch (e) {
        message.error(translate('documents.msg.error'));
        return null;
      }
    },

    /** Bulk move mixed selection via POST /document/items/move. */
    *bulkMoveItems({ payload }, { call, put }) {
      const items = payload?.items || [];
      if (!items.length) {
        message.warning(translate('documents.export.emptySelection'));
        return false;
      }
      if (items.length > MAX_DOCUMENT_BULK_ITEMS_COUNT) {
        message.error(translate('documents.bulk.maxItems', { max: MAX_DOCUMENT_BULK_ITEMS_COUNT }));
        return false;
      }
      try {
        yield call(moveDocumentItems, {
          items,
          destinationFolderId: payload?.destinationFolderId ?? null,
        });
        message.success(translate('documents.msg.bulkMoved'));
        yield put({
          type: 'fetchContents',
          payload: { page: 1, readPriority: true, silent: true },
        });
        return true;
      } catch (e) {
        message.error(translate('documents.msg.error'));
        return false;
      }
    },

    /** Bulk delete mixed selection via POST /document/items/delete (folder cascade). */
    *bulkDeleteItems({ payload }, { call, put }) {
      const items = payload?.items || [];
      if (!items.length) {
        message.warning(translate('documents.export.emptySelection'));
        return false;
      }
      if (items.length > MAX_DOCUMENT_BULK_ITEMS_COUNT) {
        message.error(translate('documents.bulk.maxItems', { max: MAX_DOCUMENT_BULK_ITEMS_COUNT }));
        return false;
      }
      try {
        yield call(deleteDocumentItems, { items });
        message.success(translate('documents.msg.bulkDeleted'));
        yield put({
          type: 'fetchContents',
          payload: { page: 1, readPriority: true, silent: true },
        });
        return true;
      } catch (e) {
        message.error(translate('documents.msg.error'));
        return false;
      }
    },

    /** Applies WebSocket `document-export` status updates to the tracked task. */
    *updateExportTask({ payload }, { put, select }) {
      const taskId = payload?.documentExportTaskId || payload?.taskId || payload?.id;
      if (!taskId) return;
      const current = yield select((s) => s.documentManager.exportTask);
      // Only update the panel if it matches the task we started (or none yet).
      if (!current?.taskId || current.taskId === taskId) {
        yield put({
          type: 'setExportTask',
          payload: {
            ...(current || {}),
            taskId,
            taskNumber: payload?.taskNumber || current?.taskNumber,
            status: payload?.taskStatus || payload?.status || current?.status,
            taskOutput: payload?.taskOutput ?? current?.taskOutput,
            observations: payload?.observations ?? current?.observations,
          },
        });
      }
      yield put({
        type: 'patchExportTaskInList',
        payload: {
          id: taskId,
          number: payload?.taskNumber,
          status: payload?.taskStatus || payload?.status,
          taskOutput: payload?.taskOutput,
          observations: payload?.observations,
          updated: payload?.updated,
        },
      });
    },

    *clearExportTask(_, { put }) {
      yield put({ type: 'setExportTask', payload: null });
    },

    /** Lists bulk-export tasks for “Mis exportaciones”. */
    *fetchExportTasks({ payload = {} }, { call, put }) {
      const page = payload.page || 1;
      const limit = payload.limit || 20;
      const append = !!payload.append;
      yield put({
        type: 'setLoading',
        payload: append ? { moreExportTasks: true } : { exportTasks: true },
      });
      try {
        const res = yield call(api.getAll, {
          route: 'document/export-tasks/paged',
          orderBy: 'created desc',
          page,
          limit,
          ...(payload.readPriority && { readPriority: true }),
        });
        yield put({
          type: 'saveExportTasks',
          payload: {
            items: res?.items || [],
            page,
            limit,
            totalCount: res?.totalCount || 0,
            append,
          },
        });
      } catch (e) {
        if (!isRequestAborted(e)) {
          message.error(translate('documents.msg.error'));
        }
      } finally {
        yield put({
          type: 'setLoading',
          payload: append ? { moreExportTasks: false } : { exportTasks: false },
        });
      }
    },
  },

  reducers: {
    setLoading(state, { payload }) {
      return {
        ...state,
        ...('folders' in payload ? { loadingFolders: payload.folders } : {}),
        ...('documents' in payload ? { loadingDocuments: payload.documents } : {}),
        ...('groups' in payload ? { loadingGroups: payload.groups } : {}),
        ...('moreDocuments' in payload ? { loadingMoreDocuments: payload.moreDocuments } : {}),
        ...('moreGroups' in payload ? { loadingMoreGroups: payload.moreGroups } : {}),
        ...('exportTasks' in payload ? { loadingExportTasks: payload.exportTasks } : {}),
        ...('moreExportTasks' in payload
          ? { loadingMoreExportTasks: payload.moreExportTasks }
          : {}),
      };
    },

    setUploadProgress(state, { payload }) {
      return { ...state, uploadProgress: payload || null };
    },

    setExportTask(state, { payload }) {
      return { ...state, exportTask: payload || null };
    },

    saveExportTasks(state, { payload }) {
      const { items, page, limit, totalCount, append } = payload || {};
      return {
        ...state,
        exportTasks: append ? [...state.exportTasks, ...(items || [])] : items || [],
        exportTasksPagination: toPaginate({ page, limit, totalCount }),
      };
    },

    patchExportTaskInList(state, { payload }) {
      const id = payload?.id;
      if (!id || !state.exportTasks?.length) return state;
      let found = false;
      const exportTasks = state.exportTasks.map((task) => {
        if (task.id !== id) return task;
        found = true;
        return {
          ...task,
          ...(payload.number != null ? { number: payload.number } : {}),
          ...(payload.status != null ? { status: payload.status } : {}),
          ...(payload.taskOutput !== undefined ? { taskOutput: payload.taskOutput } : {}),
          ...(payload.observations !== undefined ? { observations: payload.observations } : {}),
          ...(payload.updated != null ? { updated: payload.updated } : {}),
        };
      });
      if (!found) return state;
      return { ...state, exportTasks };
    },

    /** Inserts or updates a folder in the current list (no refetch). */
    upsertFolder(state, { payload }) {
      const folder = payload;
      if (!folder?.id) return state;
      const idx = state.folders.findIndex((f) => f.id === folder.id);
      const folders =
        idx >= 0
          ? state.folders.map((f, i) => (i === idx ? { ...f, ...folder } : f))
          : [folder, ...state.folders];
      const allIdx = state.allFolders.findIndex((f) => f.id === folder.id);
      const allFolders =
        state.allFolders.length === 0
          ? state.allFolders
          : allIdx >= 0
          ? state.allFolders.map((f, i) => (i === allIdx ? { ...f, ...folder } : f))
          : [...state.allFolders, folder];
      return { ...state, folders, allFolders };
    },

    patchFolder(state, { payload }) {
      const { id, ...patch } = payload || {};
      if (!id) return state;
      return {
        ...state,
        folders: state.folders.map((f) => (f.id === id ? { ...f, ...patch } : f)),
        allFolders: state.allFolders.map((f) => (f.id === id ? { ...f, ...patch } : f)),
        folderPath: state.folderPath.map((f) =>
          f.id === id ? { ...f, name: patch.name != null ? patch.name : f.name } : f,
        ),
      };
    },

    removeFolder(state, { payload }) {
      const id = payload?.id;
      if (!id) return state;
      return {
        ...state,
        folders: state.folders.filter((f) => f.id !== id),
        allFolders: state.allFolders.filter((f) => f.id !== id),
      };
    },

    patchDocument(state, { payload }) {
      const { id, ...patch } = payload || {};
      if (!id) return state;
      return {
        ...state,
        documents: state.documents.map((d) => (d.id === id ? { ...d, ...patch } : d)),
      };
    },

    removeDocument(state, { payload }) {
      const id = payload?.id;
      if (!id) return state;
      const documents = state.documents.filter((d) => d.id !== id);
      if (documents.length === state.documents.length) return state;
      const prevCount = state.pagination?.count;
      return {
        ...state,
        documents,
        pagination: {
          ...state.pagination,
          count: typeof prevCount === 'number' ? Math.max(0, prevCount - 1) : prevCount,
        },
      };
    },

    /**
     * Clears the visible list, invalidates in-flight loads (contentEpoch), and
     * sets loading flags based on the current mode.
     */
    resetContents(state) {
      return clearViewLists(state, state.groupBy, state.groupPath);
    },

    saveGroups(state, { payload }) {
      const { items, page, limit, totalCount, append, contentEpoch } = payload;
      if (contentEpoch != null && contentEpoch !== state.contentEpoch) return state;
      return {
        ...state,
        groups: append ? [...state.groups, ...(items || [])] : items || [],
        groupsPagination: toPaginate({ page, limit, totalCount }),
      };
    },

    /**
     * Switches the view mode and immediately clears folders/docs/groups so they
     * do not mix with the previous view while the fetch is in flight.
     */
    setGroupBy(state, { payload }) {
      return {
        ...clearViewLists(state, payload, []),
        groupBy: payload,
        groupPath: [],
        currentFolderId: null,
        folderPath: [],
        query: { ...state.query, page: 1, search: '' },
      };
    },

    /** Enters a virtual group: clears + loading for the destination (group docs). */
    enterGroup(state, { payload }) {
      const groupPath = [
        ...state.groupPath,
        { key: payload.key, id: payload.id, name: payload.name },
      ];
      return {
        ...clearViewLists(state, state.groupBy, groupPath),
        groupPath,
        query: { ...state.query, page: 1, search: '' },
      };
    },

    saveFolders(state, { payload }) {
      // Compat: payload may be an array (legacy) or { items, contentEpoch }.
      if (Array.isArray(payload)) {
        return { ...state, folders: payload };
      }
      const { items, contentEpoch } = payload || {};
      if (contentEpoch != null && contentEpoch !== state.contentEpoch) return state;
      return { ...state, folders: items || [] };
    },

    saveAllFolders(state, { payload }) {
      return { ...state, allFolders: payload };
    },

    saveDocuments(state, { payload }) {
      const { items, page, limit, totalCount, append, contentEpoch } = payload;
      if (contentEpoch != null && contentEpoch !== state.contentEpoch) return state;
      return {
        ...state,
        documents: append ? [...state.documents, ...(items || [])] : items || [],
        pagination: toPaginate({ page, limit, totalCount }),
      };
    },

    saveQuery(state, { payload }) {
      return { ...state, query: { ...state.query, ...payload } };
    },

    /** Clears only the search text (chip / input clear). */
    clearSearch(state) {
      return {
        ...state,
        query: { ...state.query, page: 1, search: '' },
      };
    },

    saveFilters(state, { payload }) {
      return { ...state, filters: { ...state.filters, ...payload } };
    },

    clearFilters(state) {
      return {
        ...state,
        filters: { ...emptyFilters },
        query: { ...state.query, page: 1, search: '' },
      };
    },

    /** Enters a subfolder: clears + loading to avoid an empty flash. */
    enterFolder(state, { payload }) {
      return {
        ...clearViewLists(state, 'folder', []),
        currentFolderId: payload.id,
        folderPath: [...state.folderPath, { id: payload.id, name: payload.name }],
        query: { ...state.query, page: 1, search: '' },
      };
    },

    /**
     * Navigates via breadcrumb on the active path for the current mode (real
     * folders or virtual groups). `index` null/undefined → root; otherwise
     * keeps the path up to that index (inclusive).
     * Clears lists + destination loading in the same tick.
     */
    navigateToPath(state, { payload }) {
      const { index } = payload;
      const resetQuery = { ...state.query, page: 1, search: '' };

      if (state.groupBy !== 'folder') {
        const groupPath = index == null ? [] : state.groupPath.slice(0, index + 1);
        return {
          ...clearViewLists(state, state.groupBy, groupPath),
          groupPath,
          query: resetQuery,
        };
      }

      if (index == null) {
        return {
          ...clearViewLists(state, 'folder', []),
          currentFolderId: null,
          folderPath: [],
          query: resetQuery,
        };
      }
      const folderPath = state.folderPath.slice(0, index + 1);
      const current = folderPath[folderPath.length - 1];
      return {
        ...clearViewLists(state, 'folder', []),
        folderPath,
        currentFolderId: current ? current.id : null,
        query: resetQuery,
      };
    },

    resetState() {
      return {
        ...initialState,
        query: { ...initialState.query },
        filters: { ...emptyFilters },
        folderPath: [],
        groupPath: [],
      };
    },
  },
};

export default Model;
